import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AppIcon from '../common/AppIcon';
import StatusChip from '../common/StatusChip';
import PriorityChip from '../common/PriorityChip';
import { Task } from '../../services/tasks';
import { Topic } from '../../services/topics';
import { formatDate } from '../../utils/formatDate';
import { Planner } from '../../services/planners';
import confetti from 'canvas-confetti';
import { useCurrency } from '../../context/CurrencyContext';

// ── Mobile-only config ────────────────────────────────────────
const MOBILE_STATUS_CFG: Record<string, { label: string; color: string; bg: string; stripe: string }> = {
  pending:     { label: 'To do',       color: '#7A7269', bg: '#E4DED8',                stripe: '#B5ADA5' },
  in_progress: { label: 'In progress', color: '#4A6E8A', bg: 'rgba(106,142,176,0.15)', stripe: '#6A8EB0' },
  completed:   { label: 'Done',        color: '#4A7352', bg: 'rgba(107,158,120,0.15)', stripe: '#6B9E78' },
};

const MOBILE_STATUS_SHEET_OPTIONS = [
  { value: 'pending',     label: 'To do',       desc: 'Not yet started' },
  { value: 'in_progress', label: 'In progress', desc: 'Working on it' },
  { value: 'completed',   label: 'Done',        desc: 'Task completed' },
];

// ── Mobile date formatter ─────────────────────────────────────
function fmtMobileDueDate(iso: string | null | undefined): { label: string; overdue: boolean; days: number } | null {
  if (!iso) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const d = new Date(iso + 'T00:00:00');
  const diff = Math.round((d.getTime() - today.getTime()) / 86400000);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const overdue = diff < 0;
  let label: string;
  if (diff === 0)        label = 'Today';
  else if (diff === 1)   label = 'Tomorrow';
  else if (diff === -1)  label = 'Yesterday';
  else if (diff > 1 && diff < 7)  label = dayNames[d.getDay()];
  else if (diff < -1 && diff > -7) label = `${Math.abs(diff)}d ago`;
  else                   label = `${months[d.getMonth()]} ${d.getDate()}`;
  return { label, overdue, days: diff };
}

// ── Mobile money formatter ────────────────────────────────────
function fmtMobileMoney(n: number, symbol: string): string {
  if (n >= 1000) {
    const k = n / 1000;
    return symbol + (Number.isInteger(k) ? k.toString() : k.toFixed(1)) + 'k';
  }
  return symbol + n.toLocaleString();
}

// ── Inline SVG icons (mobile only) ───────────────────────────
const CalendarIcon = ({ size = 11, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none" style={{ flexShrink: 0 }}>
    <rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke={color} strokeWidth="1.3"/>
    <path d="M1.5 5.5h11M4.5 1.5v2M9.5 1.5v2" stroke={color} strokeWidth="1.3" strokeLinecap="round"/>
  </svg>
);

const FlagIcon = ({ size = 9, color = 'currentColor' }) => (
  <svg width={size} height={size} viewBox="0 0 12 14" fill="none" style={{ flexShrink: 0 }}>
    <path d="M2 1v12M2 2h8l-2 3 2 3H2" stroke={color} strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CheckIcon = ({ size = 13, color = '#fff' }) => (
  <svg width={size} height={size} viewBox="0 0 16 16" fill="none">
    <path d="M3.5 8.5l3 3 6-7" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface TaskListProps {
  tasks: Task[];
  planners: Planner[];
  topics?: Topic[];
  loading: boolean;
  onView: (task: Task) => void;
  onEdit: (task: Task) => void;
  onDelete: (task: Task) => void;
  onStatusChange: (task: Task, status: string) => void;
  onPriorityChange: (task: Task, priority: string) => void;
  onAssigneeChange: (task: Task, plannerId: string | null) => void;
  onPinToggle?: (task: Task) => void;
  onDefaultSortChange?: (isDefault: boolean) => void;
  onResetSortRef?: React.MutableRefObject<(() => void) | null>;
}

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
];

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
];

type SortField = 'title' | 'status' | 'priority' | 'due_date';
type SortDir = 'asc' | 'desc';

const priorityOrder: Record<string, number> = { high: 0, medium: 1, low: 2 };

const defaultSort = (a: Task, b: Task): number => {
  if (a.status === 'completed' && b.status !== 'completed') return 1;
  if (a.status !== 'completed' && b.status === 'completed') return -1;
  const aPinned = a.is_pinned && a.status !== 'completed';
  const bPinned = b.is_pinned && b.status !== 'completed';
  if (aPinned && !bPinned) return -1;
  if (!aPinned && bPinned) return 1;
  if (a.due_date && b.due_date) {
    const dateCmp = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
    if (dateCmp !== 0) return dateCmp;
  } else if (a.due_date && !b.due_date) return -1;
  else if (!a.due_date && b.due_date) return 1;
  return (priorityOrder[a.priority] ?? 1) - (priorityOrder[b.priority] ?? 1);
};

const TaskList: React.FC<TaskListProps> = ({
  tasks, planners, topics = [], loading, onView, onEdit, onDelete, onStatusChange, onPriorityChange, onAssigneeChange, onPinToggle, onDefaultSortChange, onResetSortRef,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { symbol: currencySymbol } = useCurrency();
  const plannerMap = Object.fromEntries(planners.map((p) => [p.id, p]));
  const topicMap = Object.fromEntries(topics.map((t) => [t.id, t]));

  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [mobileStatusSheetTask, setMobileStatusSheetTask] = useState<Task | null>(null);
  const [actionMenuAnchor, setActionMenuAnchor] = useState<HTMLElement | null>(null);
  const [actionMenuTask, setActionMenuTask] = useState<Task | null>(null);
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<HTMLElement | null>(null);
  const [statusMenuTask, setStatusMenuTask] = useState<Task | null>(null);
  const [priorityMenuAnchor, setPriorityMenuAnchor] = useState<HTMLElement | null>(null);
  const [priorityMenuTask, setPriorityMenuTask] = useState<Task | null>(null);
  const [assigneeMenuAnchor, setAssigneeMenuAnchor] = useState<HTMLElement | null>(null);
  const [assigneeMenuTask, setAssigneeMenuTask] = useState<Task | null>(null);

  const isDefaultSort = sortField === null;

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const handleResetSort = () => {
    setSortField(null);
    setSortDir('asc');
  };

  useEffect(() => {
    onDefaultSortChange?.(isDefaultSort);
  }, [isDefaultSort, onDefaultSortChange]);

  useEffect(() => {
    if (onResetSortRef) {
      onResetSortRef.current = handleResetSort;
    }
  });

  const sortedTasks = useMemo(() => {
    if (isDefaultSort) {
      return [...tasks].sort(defaultSort);
    }
    return [...tasks].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'title':
          cmp = a.title.localeCompare(b.title);
          break;
        case 'status':
          cmp = a.status.localeCompare(b.status);
          break;
        case 'priority':
          cmp = priorityOrder[a.priority] - priorityOrder[b.priority];
          break;
        case 'due_date':
          if (!a.due_date && !b.due_date) cmp = 0;
          else if (!a.due_date) cmp = 1;
          else if (!b.due_date) cmp = -1;
          else cmp = new Date(a.due_date).getTime() - new Date(b.due_date).getTime();
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [tasks, sortField, sortDir, isDefaultSort]);

  const fireConfetti = (x: number, y: number) => {
    confetti({
      particleCount: 80,
      spread: 60,
      origin: { x: x / window.innerWidth, y: y / window.innerHeight },
      colors: ['#B07A8F', '#C9A96E', '#6B9E78', '#6A8EB0', '#D4A84E'],
    });
  };

  const handleStatusSelect = (status: string, e: React.MouseEvent) => {
    const clickX = e.clientX;
    const clickY = e.clientY;
    const task = statusMenuTask;
    const wasCompleted = task && status === 'completed' && task.status !== 'completed';
    if (task) {
      onStatusChange(task, status);
    }
    setStatusMenuAnchor(null);
    setStatusMenuTask(null);
    if (wasCompleted) {
      setTimeout(() => fireConfetti(clickX, clickY), 200);
    }
  };

  const handlePrioritySelect = (priority: string) => {
    if (priorityMenuTask) {
      onPriorityChange(priorityMenuTask, priority);
    }
    setPriorityMenuAnchor(null);
    setPriorityMenuTask(null);
  };

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const renderCard = (task: Task) => {
    const isCompleted = task.status === 'completed';
    const isOverdue = !isCompleted && !!task.due_date && new Date(task.due_date + 'T00:00:00') < today;
    const topic = task.topic_id ? topicMap[task.topic_id] : null;
    const planner = task.assigned_planner_id ? plannerMap[task.assigned_planner_id] : null;
    const dateInfo = fmtMobileDueDate(task.due_date);
    const statusCfg = MOBILE_STATUS_CFG[task.status] ?? MOBILE_STATUS_CFG.pending;
    const amountRaw = task.budget_actual ?? task.budget_expected;
    const money = amountRaw != null ? fmtMobileMoney(amountRaw, currencySymbol) : null;

    return (
      <Box
        key={task.id}
        onClick={() => onView(task)}
        sx={{
          mb: 1,
          display: 'flex',
          gap: '10px',
          alignItems: 'flex-start',
          p: '13px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: isOverdue ? 'rgba(194,106,106,0.35)' : 'grey.200',
          borderLeft: isOverdue ? '3px solid' : '1px solid',
          borderLeftColor: isOverdue ? 'error.main' : 'grey.200',
          borderRadius: '14px',
          boxShadow: '0 1px 2px rgba(61,55,49,0.03)',
          opacity: isCompleted ? 0.58 : 1,
          cursor: 'pointer',
          '&:active': { opacity: 0.8 },
        }}
      >
        {/* Checkbox — primary CTA */}
        <Box
          component="button"
          onClick={(e: React.MouseEvent) => {
            e.stopPropagation();
            const clickX = e.clientX;
            const clickY = e.clientY;
            const newStatus = task.status === 'completed' ? 'pending' : 'completed';
            onStatusChange(task, newStatus);
            if (newStatus === 'completed') {
              setTimeout(() => fireConfetti(clickX, clickY), 200);
            }
          }}
          aria-label={isCompleted ? 'Mark incomplete' : 'Mark complete'}
          sx={{
            width: 24,
            height: 24,
            borderRadius: '50%',
            border: isCompleted ? 'none' : `1.8px solid`,
            borderColor: isCompleted ? 'transparent' : isOverdue ? 'error.main' : 'primary.light',
            bgcolor: isCompleted ? 'success.main' : 'background.paper',
            color: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            p: 0,
            flexShrink: 0,
            mt: '1px',
            transition: 'all 0.15s',
            '&:active': { opacity: 0.75 },
          }}
        >
          {isCompleted && <CheckIcon size={13} color="#fff" />}
        </Box>

        {/* Main column */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '6px' }}>
          {/* Title row */}
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '6px', minWidth: 0 }}>
            {topic && (
              <span style={{ fontSize: 14, lineHeight: '19px', flexShrink: 0 }} title={topic.name}>
                {topic.emoji}
              </span>
            )}
            <Box
              component="div"
              sx={{
                flex: 1,
                minWidth: 0,
                fontSize: 15,
                fontWeight: 600,
                lineHeight: 1.3,
                letterSpacing: '-0.2px',
                color: isCompleted ? 'grey.600' : 'grey.900',
                textDecoration: isCompleted ? 'line-through' : 'none',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                wordBreak: 'break-word',
              }}
            >
              {task.title}
            </Box>
            {task.is_pinned && !isCompleted && (
              <span style={{ fontSize: 12, flexShrink: 0, marginTop: 3 }}>📌</span>
            )}
          </Box>

          {/* Meta row */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '11.5px',
              color: 'text.secondary',
              fontWeight: 500,
              minWidth: 0,
            }}
          >
            {/* Status chip */}
            <Box
              component="button"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setMobileStatusSheetTask(task);
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '4px',
                height: 20,
                px: '7px',
                borderRadius: '5px',
                border: 'none',
                bgcolor: statusCfg.bg,
                color: statusCfg.color,
                fontSize: 11,
                fontWeight: 600,
                letterSpacing: '-0.1px',
                flexShrink: 0,
                cursor: 'pointer',
                '&:active': { opacity: 0.8 },
              }}
            >
              <span style={{ width: 5, height: 5, borderRadius: '50%', background: statusCfg.stripe, flexShrink: 0 }} />
              {statusCfg.label}
            </Box>

            {/* Priority flag — hidden for low */}
            {task.priority === 'high' && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#C26A6A', fontSize: 11, fontWeight: 500, flexShrink: 0 }}>
                <FlagIcon size={9} color="#C26A6A" />
                High
              </span>
            )}
            {task.priority === 'medium' && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, color: '#A68845', fontSize: 11, fontWeight: 500, flexShrink: 0 }}>
                <FlagIcon size={9} color="#D4A84E" />
                Medium
              </span>
            )}

            {/* Due date */}
            {dateInfo && (
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 3,
                color: isOverdue ? '#C26A6A' : undefined,
                fontWeight: isOverdue ? 600 : 500,
                flexShrink: 0,
              }}>
                <CalendarIcon size={11} color={isOverdue ? '#C26A6A' : '#B5ADA5'} />
                {dateInfo.label}
              </span>
            )}

            {/* Spacer */}
            <Box sx={{ flex: 1 }} />

            {/* Budget */}
            {money && (
              <span style={{ fontVariantNumeric: 'tabular-nums', flexShrink: 0, fontSize: 11.5, fontWeight: 500 }}>
                {money}
              </span>
            )}

            {/* Planner avatar */}
            {planner && (
              <Box
                title={planner.name}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: '50%',
                  bgcolor: planner.color || 'grey.400',
                  color: '#fff',
                  fontSize: Math.round(18 * 0.48),
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  letterSpacing: 0,
                }}
              >
                {planner.name[0]}
              </Box>
            )}
          </Box>
        </Box>
      </Box>
    );
  };

  const renderRow = (task: Task) => {
    const isCompleted = task.status === 'completed';
    const isOverdue = !isCompleted && task.due_date && new Date(task.due_date) < today;
    const topic = task.topic_id ? topicMap[task.topic_id] : null;
    return (
      <TableRow
        key={task.id}
        hover
        onClick={() => onView(task)}
        sx={{
          cursor: 'pointer',
          opacity: isCompleted ? 0.55 : 1,
          '&:hover .delete-action': { opacity: 1 },
          ...(isCompleted && {
            bgcolor: 'rgba(107,158,120,0.04)',
          }),
          ...(isOverdue && {
            borderLeft: '3px solid',
            borderLeftColor: 'error.main',
            bgcolor: 'rgba(194,106,106,0.04)',
          }),
        }}
      >
        <TableCell sx={{ pl: 2, py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {task.is_pinned && !isCompleted && <span style={{ fontSize: 14 }}>📌</span>}
            {isCompleted && <AppIcon name="check-circle" fontSize="small" sx={{ color: '#6B9E78' }} />}
            <Typography
              variant="body2"
              sx={{
                fontWeight: 500,
                textDecoration: isCompleted ? 'line-through' : 'none',
                color: isCompleted ? 'text.secondary' : 'text.primary',
              }}
            >
              {task.title}
            </Typography>
          </Box>
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          {topic ? (
            <Chip
              label={`${topic.emoji || ''} ${topic.name}`.trim()}
              size="small"
              sx={{ bgcolor: 'grey.100', fontWeight: 500 }}
            />
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.5 }}>—</Typography>
          )}
        </TableCell>
        <TableCell sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
          <Box
            onClick={(e) => {
              setStatusMenuAnchor(e.currentTarget as HTMLElement);
              setStatusMenuTask(task);
            }}
            sx={{ cursor: 'pointer', display: 'inline-block' }}
          >
            <StatusChip status={task.status} />
          </Box>
        </TableCell>
        <TableCell sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
          <Box
            onClick={(e) => {
              setPriorityMenuAnchor(e.currentTarget as HTMLElement);
              setPriorityMenuTask(task);
            }}
            sx={{ cursor: 'pointer', display: 'inline-block' }}
          >
            <PriorityChip priority={task.priority} />
          </Box>
        </TableCell>
        <TableCell sx={{ py: 1.5, color: isOverdue ? 'error.main' : 'inherit', fontWeight: isOverdue ? 600 : 'inherit' }}>
          {task.due_date ? formatDate(task.due_date) : ''}
        </TableCell>
        <TableCell sx={{ py: 1.5 }}>
          {(task.budget_actual != null || task.budget_expected != null) ? (
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              {currencySymbol}{(task.budget_actual ?? task.budget_expected ?? 0).toLocaleString()}
            </Typography>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.5 }}>—</Typography>
          )}
        </TableCell>
        <TableCell sx={{ py: 1.5 }} onClick={(e) => e.stopPropagation()}>
          <Box
            onClick={(e) => {
              setAssigneeMenuAnchor(e.currentTarget as HTMLElement);
              setAssigneeMenuTask(task);
            }}
            sx={{ cursor: 'pointer', display: 'inline-block', minWidth: 24, minHeight: 24 }}
          >
            {task.assigned_planner_id && plannerMap[task.assigned_planner_id] ? (
              <Chip
                label={plannerMap[task.assigned_planner_id].name}
                size="small"
                sx={{
                  bgcolor: plannerMap[task.assigned_planner_id].color
                    ? `${plannerMap[task.assigned_planner_id].color}25`
                    : 'grey.100',
                  color: plannerMap[task.assigned_planner_id].color || 'grey.800',
                  fontWeight: 500,
                  border: `1px solid ${plannerMap[task.assigned_planner_id].color || '#E4DED8'}`,
                }}
              />
            ) : (
              <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.5 }}>—</Typography>
            )}
          </Box>
        </TableCell>
        <TableCell sx={{ py: 1.5, width: 48 }} onClick={(e) => e.stopPropagation()}>
          <Tooltip title="More">
            <IconButton
              size="small"
              className="delete-action"
              sx={{ opacity: 0, transition: 'opacity 0.2s' }}
              onClick={(e) => {
                setActionMenuAnchor(e.currentTarget);
                setActionMenuTask(task);
              }}
            >
              <AppIcon name="more-vert" fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>
    );
  };

  if (loading) return null;

  return (
    <>
      {/* Mobile: card list */}
      {isMobile ? (
        <Box>
          {sortedTasks.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No tasks yet
            </Typography>
          ) : (
            sortedTasks.map((task) => renderCard(task))
          )}
        </Box>
      ) : (
        /* Desktop: unchanged table */
        <TableContainer
          sx={{
            border: '1px solid',
            borderColor: 'grey.200',
            borderRadius: 3,
            bgcolor: 'background.paper',
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600 }}>
                  <TableSortLabel active={sortField === 'title'} direction={sortField === 'title' ? sortDir : 'asc'} onClick={() => handleSort('title')}>
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 160 }}>Topic</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 130 }}>
                  <TableSortLabel active={sortField === 'status'} direction={sortField === 'status' ? sortDir : 'asc'} onClick={() => handleSort('status')}>
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 110 }}>
                  <TableSortLabel active={sortField === 'priority'} direction={sortField === 'priority' ? sortDir : 'asc'} onClick={() => handleSort('priority')}>
                    Priority
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 120 }}>
                  <TableSortLabel active={sortField === 'due_date'} direction={sortField === 'due_date' ? sortDir : 'asc'} onClick={() => handleSort('due_date')}>
                    Due Date
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, width: 100 }}>Budget</TableCell>
                <TableCell sx={{ fontWeight: 600, width: 140 }}>Planner</TableCell>
                <TableCell sx={{ width: 48 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedTasks.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">No tasks yet</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedTasks.map((task) => renderRow(task))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Action context menu */}
      <Menu
        anchorEl={actionMenuAnchor}
        open={!!actionMenuAnchor}
        onClose={() => { setActionMenuAnchor(null); setActionMenuTask(null); }}
      >
        {onPinToggle && (
          <MenuItem
            onClick={() => {
              if (actionMenuTask) onPinToggle(actionMenuTask);
              setActionMenuAnchor(null);
              setActionMenuTask(null);
            }}
          >
            {actionMenuTask?.is_pinned ? '📌 Unpin' : '📌 Pin'}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (actionMenuTask) onEdit(actionMenuTask);
            setActionMenuAnchor(null);
            setActionMenuTask(null);
          }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (actionMenuTask) onDelete(actionMenuTask);
            setActionMenuAnchor(null);
            setActionMenuTask(null);
          }}
          sx={{ color: 'error.main' }}
        >
          Delete
        </MenuItem>
      </Menu>

      {/* Status picker menu */}
      <Menu
        anchorEl={statusMenuAnchor}
        open={!!statusMenuAnchor}
        onClose={() => { setStatusMenuAnchor(null); setStatusMenuTask(null); }}
      >
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={statusMenuTask?.status === opt.value}
            onClick={(e) => handleStatusSelect(opt.value, e)}
          >
            <StatusChip status={opt.value} />
          </MenuItem>
        ))}
      </Menu>

      {/* Priority picker menu */}
      <Menu
        anchorEl={priorityMenuAnchor}
        open={!!priorityMenuAnchor}
        onClose={() => { setPriorityMenuAnchor(null); setPriorityMenuTask(null); }}
      >
        {PRIORITY_OPTIONS.map((opt) => (
          <MenuItem
            key={opt.value}
            selected={priorityMenuTask?.priority === opt.value}
            onClick={() => handlePrioritySelect(opt.value)}
          >
            <PriorityChip priority={opt.value} />
          </MenuItem>
        ))}
      </Menu>

      {/* Assignee picker menu */}
      <Menu
        anchorEl={assigneeMenuAnchor}
        open={!!assigneeMenuAnchor}
        onClose={() => { setAssigneeMenuAnchor(null); setAssigneeMenuTask(null); }}
      >
        <MenuItem
          selected={!assigneeMenuTask?.assigned_planner_id}
          onClick={() => {
            if (assigneeMenuTask) onAssigneeChange(assigneeMenuTask, null);
            setAssigneeMenuAnchor(null);
            setAssigneeMenuTask(null);
          }}
        >
          <Typography variant="body2" color="text.secondary">None</Typography>
        </MenuItem>
        {planners.map((p) => (
          <MenuItem
            key={p.id}
            selected={assigneeMenuTask?.assigned_planner_id === p.id}
            onClick={() => {
              if (assigneeMenuTask) onAssigneeChange(assigneeMenuTask, p.id);
              setAssigneeMenuAnchor(null);
              setAssigneeMenuTask(null);
            }}
          >
            <Chip
              label={p.name}
              size="small"
              sx={{
                bgcolor: p.color ? `${p.color}25` : 'grey.100',
                color: p.color || 'grey.800',
                fontWeight: 500,
                border: `1px solid ${p.color || '#E4DED8'}`,
              }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* Mobile status action sheet */}
      <Drawer
        anchor="bottom"
        open={!!mobileStatusSheetTask}
        onClose={() => setMobileStatusSheetTask(null)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            p: 1,
          },
        }}
      >
        {mobileStatusSheetTask && (
          <Box>
            {/* Task context header */}
            <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Change status
              </Typography>
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 600,
                  color: 'grey.900',
                  letterSpacing: '-0.2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  mt: '2px',
                }}
              >
                {mobileStatusSheetTask.title}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {MOBILE_STATUS_SHEET_OPTIONS.map((opt) => {
                const cfg = MOBILE_STATUS_CFG[opt.value] ?? MOBILE_STATUS_CFG.pending;
                const isActive = mobileStatusSheetTask.status === opt.value;
                return (
                  <Box
                    key={opt.value}
                    component="button"
                    onClick={() => {
                      const task = mobileStatusSheetTask;
                      const wasCompleted = opt.value === 'completed' && task.status !== 'completed';
                      onStatusChange(task, opt.value);
                      setMobileStatusSheetTask(null);
                      if (wasCompleted) {
                        // Use center of screen as fallback position
                        setTimeout(() => fireConfetti(window.innerWidth / 2, window.innerHeight * 0.6), 200);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      px: '14px',
                      py: '12px',
                      bgcolor: isActive ? 'grey.50' : 'transparent',
                      border: 'none',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      textAlign: 'left',
                      '&:active': { opacity: 0.8 },
                    }}
                  >
                    <Box
                      sx={{
                        width: 10,
                        height: 10,
                        borderRadius: '50%',
                        bgcolor: cfg.stripe,
                        flexShrink: 0,
                        boxShadow: `0 0 0 3px ${cfg.bg}`,
                      }}
                    />
                    <Box sx={{ flex: 1 }}>
                      <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'grey.900', lineHeight: 1.3 }}>
                        {opt.label}
                      </Typography>
                      <Typography sx={{ fontSize: '11.5px', color: 'text.secondary', mt: '1px' }}>
                        {opt.desc}
                      </Typography>
                    </Box>
                    {isActive && <CheckIcon size={14} color="#B07A8F" />}
                  </Box>
                );
              })}
            </Box>
            <Box
              component="button"
              onClick={() => setMobileStatusSheetTask(null)}
              sx={{
                display: 'block',
                width: '100%',
                height: 46,
                mt: '6px',
                border: 'none',
                borderRadius: '12px',
                bgcolor: 'grey.100',
                color: 'grey.800',
                fontWeight: 600,
                fontSize: 14,
                cursor: 'pointer',
                '&:active': { opacity: 0.8 },
              }}
            >
              Cancel
            </Box>
          </Box>
        )}
      </Drawer>
    </>
  );
};

export default TaskList;
